/* eslint-disable */
import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, Link, NavLink } from 'react-router-dom'
import { Box, CssBaseline } from '@mui/material'
import {
    Calendar,
    Bot,
    X,
    Home,
    Building2,
    BookOpen,
    Bell,
    Shield,
} from 'lucide-react'
import axios from 'axios'
import Sidebar from './Sidebar'
import NotificationDrawer from './NotificationDrawer'
import ProfilePopover from './ProfilePopover'
import VirtualTA from '../commons/VirtualTA'
import Footer from '../commons/Footer'
import { isAdmin as checkIsAdmin } from '../../utils/auth'
import { useTenant } from '../../context/TenantContext'
import './MainLayout.css'

function MainLayout({
    children,
    hideNav = false,
    hideSidebar = false,
    hideTA = false,
    isValidated = false,
}) {
    const { isTenant, tenantSlug, orgData } = useTenant()
    const [logoError, setLogoError] = useState(false)

    useEffect(() => {
        setLogoError(false)
    }, [orgData?.logo, orgData?.logoUrl])

    const orgDisplayName = orgData?.name || tenantSlug || 'Organisation'
    const orgLogoUrl = !logoError && (orgData?.logo || orgData?.logoUrl) ? (orgData?.logo || orgData?.logoUrl) : null
    const rawHomepage = orgData?.homepageUrl?.trim()
    const orgHomepage = rawHomepage
        ? /^https?:\/\//i.test(rawHomepage)
            ? rawHomepage
            : `https://${rawHomepage}`
        : null
    const showLayout = isValidated && !hideNav

    const [isNotificationOpen, setIsNotificationOpen] = useState(false)
    const [isProfileOpen, setIsProfileOpen] = useState(false)
    const [isAiOpen, setIsAiOpen] = useState(false)

    // Resizer states
    const [aiWidth, setAiWidth] = useState(400)
    const [isDragging, setIsDragging] = useState(false)

    const [dateStr, setDateStr] = useState('')
    const location = useLocation()
    const userData = useSelector((state) => state.userData || state.user)

    // Extract courseId from the current URL so VirtualTA always reflects the active course
    const activeCourseIdFromUrl = (() => {
        const match = location.pathname.match(
            /\/course(?:s)?\/([\/\w\-]+?)(?:\/|$)/
        )
        return match ? match[1] : null
    })()

    const [notifications, setNotifications] = useState([])
    const [courses, setCourses] = useState([])

    useEffect(() => {
        console.log('orgData changed', orgData)
    }, [orgData])


    const getNotifTime = (n) => {
        if (n.createdAt) {
            const t = new Date(n.createdAt).getTime()
            if (!isNaN(t) && t > 0) return t
        }
        if (typeof n._id === 'string' && n._id.length >= 24) {
            const hex = n._id.substring(0, 24)
            if (/^[0-9a-fA-F]{24}$/.test(hex)) {
                const t = parseInt(hex.substring(0, 8), 16) * 1000
                if (!isNaN(t) && t > 0) return t
            }
        }
        return 0
    }

    const buildCombinedNotifications = (backendNotifs = []) => {
        const deletedIds = JSON.parse(
            localStorage.getItem('myacademic_deleted_notifs') || '[]'
        )

        const seenMap = new Map()
        backendNotifs.forEach((n) => {
            if (!deletedIds.includes(n._id) && !seenMap.has(n._id)) {
                seenMap.set(n._id, n)
            }
        })
        const finalNotifs = Array.from(seenMap.values())
        finalNotifs.sort((a, b) => getNotifTime(b) - getNotifTime(a))
        return finalNotifs
    }

    const fetchNotificationsOnly = async () => {
        try {
            const notifRes = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/user/notifications`,
                { withCredentials: true }
            )
            const backendNotifs = (notifRes.data?.notifications || []).filter(
                (n) =>
                    n.type !== 'welcome' &&
                    !n.notification?.toLowerCase().includes('welcome')
            )
            setNotifications(buildCombinedNotifications(backendNotifs))
        } catch (e) {
            console.error('Error polling notifications:', e)
        }
    }

    const fetchAllData = async () => {
        try {
            // 1. Fetch courses
            const coursesRes = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/api/v2/courses`,
                { withCredentials: true }
            )
            const enrolledCourses = coursesRes.data || []
            setCourses(enrolledCourses)

            // 2. Fetch notifications (including assignments and quizzes from backend)
            await fetchNotificationsOnly()
        } catch (e) {
            console.error('Error fetching layout data:', e)
        }
    }

    const handleMarkAllRead = async () => {
        try {
            await axios.patch(
                `${process.env.REACT_APP_BASE_URL}/v3/notifications/mark-all-seen`,
                {},
                { withCredentials: true }
            )
        } catch (e) {
            console.error('Failed to mark all as seen:', e)
        }
        const seenIds = JSON.parse(
            localStorage.getItem('myacademic_seen_notifs') || '[]'
        )
        notifications.forEach((n) => {
            if (
                n._id.startsWith('assignment-') ||
                n._id === 'welcome-notification'
            ) {
                if (!seenIds.includes(n._id)) {
                    seenIds.push(n._id)
                }
            }
        })
        localStorage.setItem('myacademic_seen_notifs', JSON.stringify(seenIds))
        setNotifications((prev) => prev.map((n) => ({ ...n, seen: true })))
    }

    const handleMarkSeen = async (notifId) => {
        if (notifId === 'welcome-notification') {
            localStorage.setItem('myacademic_welcome_shown', 'true')
        }
        if (
            notifId.startsWith('assignment-') ||
            notifId === 'welcome-notification'
        ) {
            const seenIds = JSON.parse(
                localStorage.getItem('myacademic_seen_notifs') || '[]'
            )
            if (!seenIds.includes(notifId)) {
                seenIds.push(notifId)
                localStorage.setItem(
                    'myacademic_seen_notifs',
                    JSON.stringify(seenIds)
                )
            }
        } else {
            try {
                await axios.patch(
                    `${process.env.REACT_APP_BASE_URL}/v3/notifications/${notifId}/seen`,
                    {},
                    { withCredentials: true }
                )
            } catch (e) {
                console.error('Failed to mark notification as seen:', e)
            }
        }
        setNotifications((prev) =>
            prev.map((n) => (n._id === notifId ? { ...n, seen: true } : n))
        )
    }

    const handleDelete = async (notifId) => {
        if (notifId === 'welcome-notification') {
            localStorage.setItem('myacademic_welcome_shown', 'true')
        }
        if (
            notifId.startsWith('assignment-') ||
            notifId === 'welcome-notification'
        ) {
            const deletedIds = JSON.parse(
                localStorage.getItem('myacademic_deleted_notifs') || '[]'
            )
            if (!deletedIds.includes(notifId)) {
                deletedIds.push(notifId)
                localStorage.setItem(
                    'myacademic_deleted_notifs',
                    JSON.stringify(deletedIds)
                )
            }
        } else {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_BASE_URL}/v3/notifications/${notifId}`,
                    { withCredentials: true }
                )
            } catch (e) {
                console.error('Failed to delete notification:', e)
            }
        }
        setNotifications((prev) => prev.filter((n) => n._id !== notifId))
    }

    useEffect(() => {
        if (!showLayout) return
        const days = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
        ]
        const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ]
        const now = new Date()
        setDateStr(
            `${days[now.getDay()]} • ${months[now.getMonth()]
            } ${now.getDate()}, ${now.getFullYear()}`
        )

        // Setup notification check endings
        const checkCourseEndings = async () => {
            try {
                await axios.get(
                    `${process.env.REACT_APP_BASE_URL}/v3/notifications/check-course-endings`,
                    { withCredentials: true }
                )
            } catch (e) {
                console.error('Failed to check course endings:', e)
            }
        }

        checkCourseEndings()
        fetchAllData()

        const intervalNotifs = setInterval(fetchNotificationsOnly, 120000) // Poll every 2 minutes
        const intervalEndings = setInterval(checkCourseEndings, 300000)

        return () => {
            clearInterval(intervalNotifs)
            clearInterval(intervalEndings)
        }
    }, [showLayout])

    // Global drag-to-resize handlers
    useEffect(() => {
        if (!isDragging) return

        const handleMouseMove = (e) => {
            let newWidth = window.innerWidth - e.clientX
            const minWidth = 400
            const maxWidth = window.innerWidth * 0.5 // 50vw

            if (newWidth < minWidth) newWidth = minWidth
            if (newWidth > maxWidth) newWidth = maxWidth

            setAiWidth(newWidth)
        }

        const handleMouseUp = () => {
            setIsDragging(false)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''

            // Set temporary flag to avoid triggering outside-click close
            window.justFinishedDragging = true
            setTimeout(() => {
                window.justFinishedDragging = false
            }, 50)
        }

        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)

        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isDragging])

    useEffect(() => {
        if (location.pathname === '/virtual-ta' || hideTA) {
            setIsAiOpen(false)
        }
    }, [location.pathname, hideTA])

    const toggleNotificationDrawer = () => {
        setIsNotificationOpen(!isNotificationOpen)
        if (isProfileOpen) setIsProfileOpen(false)
        if (isAiOpen) setIsAiOpen(false)
    }

    const toggleProfilePopover = (e) => {
        e.stopPropagation()
        setIsProfileOpen(!isProfileOpen)
        if (isNotificationOpen) setIsNotificationOpen(false)
        if (isAiOpen) setIsAiOpen(false)
    }

    const toggleAiChat = (e) => {
        e.stopPropagation()
        const nextState = !isAiOpen
        setIsAiOpen(nextState)
        if (nextState) {
            setIsNotificationOpen(false)
            setIsProfileOpen(false)
        }
    }

    const closeAll = () => {
        if (window.justFinishedDragging) return
        if (isProfileOpen) setIsProfileOpen(false)
        if (isNotificationOpen) setIsNotificationOpen(false)
        if (isAiOpen) setIsAiOpen(false)
    }

    const getHeaderTitle = (pathname) => {
        if (pathname === '/' || pathname === '/dashboard')
            return 'Academic Dashboard'
        if (pathname === '/courses' || pathname.startsWith('/course/'))
            return 'My Courses'
        if (pathname === '/calendar') return 'Academic Calendar'
        if (pathname === '/org') return 'Organizations'
        if (pathname === '/performance-analytics')
            return 'Performance Analytics'
        if (pathname === '/notes') return 'My Notes'
        if (pathname === '/myaccount') return 'My Account'
        return 'myAcademic Console'
    }

    const handleMouseDown = (e) => {
        e.preventDefault()
        setIsDragging(true)
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
    }

    const user = userData?.user
    const isLoggedIn = !!user && user.id !== undefined
    const isAdmin = isLoggedIn && checkIsAdmin(user)
    const name = user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
        user.name ||
        'User Profile'
        : 'User Profile'
    const unreadCount = notifications.filter((n) => !n.seen).length
    const hasOrg = courses && courses.some((course) => !!course.organizationId)

    useEffect(() => {
        if (unreadCount > 0) {
            document.title = `(${unreadCount}) myAcademic`
        } else {
            document.title = 'myAcademic'
        }
    }, [unreadCount])

    const getInitials = (nameStr) => {
        if (!nameStr || nameStr === 'User Profile') return 'UP'
        const parts = nameStr.split(' ').filter(Boolean)
        if (parts.length === 0) return 'UP'
        if (parts.length === 1) return parts[0][0].toUpperCase()
        return (parts[0][0] + parts[1][0]).toUpperCase()
    }

    const hideLayout = !isValidated || hideNav
    const isQuizAttendanceRoute =
        location.pathname.match(/\/quiz\/(v2|v3|join|instructor)\b/) ||
        location.pathname.match(/\/courses\/canvas\/[^\/]+\/quiz/)

    return (
        <Box
            className={`main-layout-root ${hideLayout
                ? 'hide-navigation-layout'
                : hideSidebar
                    ? 'hide-sidebar-layout'
                    : ''
                }`}
            onClick={closeAll}
        >
            <CssBaseline />

            <Sidebar
                isNotificationOpen={isNotificationOpen}
                isProfileOpen={isProfileOpen}
                toggleNotificationDrawer={toggleNotificationDrawer}
                toggleProfilePopover={toggleProfilePopover}
                unreadCount={unreadCount}
            />

            <NotificationDrawer
                isOpen={isNotificationOpen}
                onClose={() => setIsNotificationOpen(false)}
                notifications={notifications}
                onMarkAllRead={handleMarkAllRead}
                onMarkSeen={handleMarkSeen}
                onDelete={handleDelete}
                courses={courses}
            />

            <ProfilePopover
                isOpen={isProfileOpen}
                onClose={() => setIsProfileOpen(false)}
                user={userData?.user}
            />

            {/* Responsive Bottom Navigation Bar */}
            {isLoggedIn && !hideLayout && !isQuizAttendanceRoute && (
                <div
                    className="responsive-bottom-bar"
                    onClick={(e) => e.stopPropagation()}
                >
                    <NavLink
                        exact
                        activeClassName="bottom-bar-item-active"
                        className="bottom-bar-item"
                        to="/"
                    >
                        <Home size={22} />
                        <span>Home</span>
                    </NavLink>

                    {(() => {
                        if (!hasOrg && !isAdmin) return null
                        const coursesWithOrg = courses
                            ? courses.filter(
                                (course) => !!course.organizationId
                            )
                            : []
                        const orgLink =
                            !isAdmin && coursesWithOrg.length === 1
                                ? `/course/${coursesWithOrg[0].id}`
                                : '/org'
                        return (
                            <NavLink
                                activeClassName="bottom-bar-item-active"
                                className="bottom-bar-item"
                                to={orgLink}
                            >
                                <Building2 size={22} />
                                <span>Org</span>
                            </NavLink>
                        )
                    })()}

                    <NavLink
                        activeClassName="bottom-bar-item-active"
                        className="bottom-bar-item"
                        to="/courses"
                    >
                        <BookOpen size={22} />
                        <span>Courses</span>
                    </NavLink>

                    {isAdmin ? (
                        <NavLink
                            activeClassName="bottom-bar-item-active"
                            className="bottom-bar-item"
                            to="/admin-panel"
                        >
                            <Shield size={22} />
                            <span>Admin Panel</span>
                        </NavLink>
                    ) : (
                        <button
                            className={`bottom-bar-item notification-btn ${isNotificationOpen ? 'active' : ''
                                }`}
                            id="bellBtnMobile"
                            onClick={toggleNotificationDrawer}
                            type="button"
                            style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                            }}
                        >
                            <div className="bottom-bar-icon-wrapper">
                                <Bell size={22} />
                                {unreadCount > 0 && (
                                    <div className="bottom-bar-badge">
                                        {unreadCount}
                                    </div>
                                )}
                            </div>
                            <span>Notification</span>
                        </button>
                    )}

                    <div
                        className={`bottom-bar-item avatar-container ${isProfileOpen ? 'active' : ''
                            }`}
                        id="avatarBtnMobile"
                        onClick={toggleProfilePopover}
                    >
                        <div className="bottom-bar-avatar">
                            {getInitials(name)}
                        </div>
                        <span>Profile</span>
                    </div>
                </div>
            )}

            <Box id="appWorkArea" onClick={(e) => e.stopPropagation()}>
                {/* TOP APP BAR - PERMANENTLY FIXED AT TOP */}
                <Box component="header" className="app-header">
                    <div className="header-title-area">
                        <Link
                            to="/"
                            className="brand-logo-link"
                            style={{
                                textDecoration: 'none',
                                color: 'inherit',
                                display: 'inline-flex',
                                alignItems: 'center',
                            }}
                        >
                            <h2 id="headerTitle" className="header-brand-title">
                                <i>my</i>Academic
                            </h2>
                        </Link>

                        {isTenant && (
                            <>
                                <span className="header-tenant-divider" />
                                {orgHomepage ? (
                                    <a
                                        href={orgHomepage}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title={orgDisplayName}
                                        className="header-org-link"
                                    >
                                        {orgLogoUrl ? (
                                            <img
                                                alt={orgDisplayName}
                                                src={orgLogoUrl}
                                                style={{
                                                    height: '36px',
                                                    maxHeight: '36px',
                                                    maxWidth: '140px',
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                }}
                                                onError={() => setLogoError(true)}
                                            />
                                        ) : (
                                            <span className="header-org-name">{orgDisplayName}</span>
                                        )}
                                    </a>
                                ) : (
                                    <Link
                                        to="/"
                                        title={orgDisplayName}
                                        className="header-org-link"
                                    >
                                        {orgLogoUrl ? (
                                            <img
                                                alt={orgDisplayName}
                                                src={orgLogoUrl}
                                                style={{
                                                    height: '36px',
                                                    maxHeight: '36px',
                                                    maxWidth: '140px',
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                }}
                                                onError={() => setLogoError(true)}
                                            />
                                        ) : (
                                            <span className="header-org-name">{orgDisplayName}</span>
                                        )}
                                    </Link>
                                )}
                            </>
                        )}
                    </div>
                    <div className="header-meta">
                        <Link
                            to="/calendar"
                            style={{
                                textDecoration: 'none',
                                color: 'inherit',
                            }}
                        >
                            <div className="current-date-badge">
                                <Calendar
                                    style={{
                                        width: '14px',
                                        height: '14px',
                                    }}
                                />
                                <span id="currentDateStr">{dateStr}</span>
                            </div>
                        </Link>
                        {!location.pathname.includes('/virtual-ta') &&
                            !hideTA && (
                                <button
                                    className={`ai-trigger-btn ${isAiOpen ? 'active' : ''
                                        }`}
                                    id="aiTriggerBtn"
                                    onClick={toggleAiChat}
                                    type="button"
                                >
                                    <Bot
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                        }}
                                    />
                                    <span className="virtual-ta-text">
                                        Virtual TA
                                    </span>
                                </button>
                            )}
                    </div>
                </Box>

                {/* WORK CONTENT CONTAINER (SINGLE SCROLL AREA FOR PAGE CONTENT & FOOTER) */}
                <Box className="work-content-container">
                    <Box
                        component="main"
                        className="content-body"
                        id="main-container"
                    >
                        <div className="page-content-wrapper">{children}</div>
                        {!hideLayout && <Footer />}
                    </Box>

                    {/* AI PANEL SPLIT RESIZER */}
                    <div
                        id="aiResizer"
                        className={`ai-resizer ${isDragging ? 'dragging' : ''}`}
                        style={{ display: isAiOpen ? 'block' : 'none' }}
                        onMouseDown={handleMouseDown}
                    />

                    {/* VIRTUAL TA SIDEBAR PANEL */}
                    <div
                        id="aiChatDrawer"
                        style={{
                            display: isAiOpen ? 'flex' : 'none',
                            flexDirection: 'column',
                            height: '100%',
                            overflow: 'hidden',
                            width: `${aiWidth}px`,
                            minWidth: `${aiWidth}px`,
                            maxWidth: `${aiWidth}px`,
                            position: 'relative',
                            flexShrink: 0,
                        }}
                    >
                        <VirtualTA
                            courseId={activeCourseIdFromUrl}
                            handleClose={() => setIsAiOpen(false)}
                        />
                    </div>
                </Box>
            </Box>
        </Box>
    )
}

export default MainLayout
